import { lazy, Suspense } from 'react'

import FallbackImage from './imageFallback.svg'
import { SkeletonLoader } from '../../ui/SkeletonLoader'
import { Div } from '../Div'
import { Image } from '../Image'
import type { ImageProps } from '../Image'
import type { DynamicLottieProps as ResponsiveLottieProps } from '../Lottie'
import type { SvgProps } from '../Svg'
import { Svg } from '../Svg'
import type { VideoPlayerProps } from '../VideoPlayer'

const LazyVideoPlayer = lazy(async () => ({
  default: (await import('../VideoPlayer')).VideoPlayer,
}))

const LazyLottiePlayer = lazy(async () => ({
  default: (await import('../Lottie/DynamicLottie')).DynamicLottie,
}))

export type MediaType = 'image' | 'video' | 'lotti'

export type MediaProps = {
  mediaType: 'lotti' | 'video' | 'image' | 'svg'
} & Partial<ResponsiveLottieProps> &
  Partial<VideoPlayerProps> &
  Partial<ImageProps> &
  Partial<SvgProps>

export function Media(props: MediaProps) {
  return props.mediaType === 'image' ? (
    <MediaComponent {...props} />
  ) : (
    <Suspense fallback={<SkeletonLoader />}>
      <MediaComponent {...props} />
    </Suspense>
  )
}

export function MediaComponent({
  mediaType,
  thumbnailAlt,
  thumbnailUrl,
  hasLightbox,
  ...mediaProps
}: MediaProps) {
  if (mediaType === 'image') {
    return (
      <Image
        alt="default alt"
        {...(mediaProps as ImageProps)}
        src={(mediaProps as ImageProps).src || (FallbackImage as any)}
      />
    )
  }

  if (mediaType === 'svg') {
    return <Svg {...(mediaProps as SvgProps)} />
  }

  if (mediaType === 'video') {
    return (
      <Div flexDirection="column" width="100%" justifyContent="center" flex={1}>
        <LazyVideoPlayer
          {...(mediaProps as VideoPlayerProps)}
          thumbnailAlt={thumbnailAlt}
          thumbnailUrl={thumbnailUrl}
          hasLightbox={hasLightbox}
        />
      </Div>
    )
  }
  if (mediaType === 'lotti') {
    return (
      <Div flexDirection="column" width="100%" justifyContent="center">
        <LazyLottiePlayer {...(mediaProps as ResponsiveLottieProps)} />
      </Div>
    )
  }

  return null
}
